import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePlayerStore } from '../../stores/usePlayerStore'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

interface Machinery {
  id: string
  name: string
  type: 'production' | 'transport' | 'storage'
  level: number
  efficiency: number
  maintenance_cost: number
  purchase_price: number
  condition: number
  purchased_at: string
  is_active: boolean
}

interface MachineryCatalog {
  id: string
  name: string
  type: 'production' | 'transport' | 'storage'
  level: number
  base_efficiency: number
  purchase_price: number
  maintenance_cost: number
  required_level: number
  description?: string
}

export const MachineryShop: React.FC = () => {
  const { t } = useTranslation()
  const { player } = usePlayerStore()
  const [machinery, setMachinery] = useState<Machinery[]>([])
  const [catalog, setCatalog] = useState<MachineryCatalog[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedType, setSelectedType] = useState<string>('all')

  useEffect(() => {
    if (player?.id) {
      loadMachinery()
      loadCatalog()
    }
  }, [player?.id])

  const loadMachinery = async () => {
    setIsLoading(true)
    try {
      // Mock data for owned machinery
      const mockMachinery: Machinery[] = [
        {
          id: 'mach_1',
          name: 'دستگاه تولید نان',
          type: 'production',
          level: 2,
          efficiency: 1.3,
          maintenance_cost: 100,
          purchase_price: 5000,
          condition: 0.85,
          purchased_at: new Date().toISOString(),
          is_active: true
        },
        {
          id: 'mach_2',
          name: 'وانت حمل و نقل',
          type: 'transport',
          level: 1,
          efficiency: 1.1,
          maintenance_cost: 50,
          purchase_price: 3000,
          condition: 0.95,
          purchased_at: new Date(Date.now() - 86400000).toISOString(),
          is_active: true
        }
      ]
      setMachinery(mockMachinery)
    } catch (error) {
      console.error('Error loading machinery:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadCatalog = async () => {
    try {
      // Mock data for machinery catalog
      const mockCatalog: MachineryCatalog[] = [
        {
          id: 'cat_1',
          name: 'دستگاه تولید شیر',
          type: 'production',
          level: 3,
          base_efficiency: 1.5,
          purchase_price: 8000,
          maintenance_cost: 150,
          required_level: 3,
          description: 'دستگاه پیشرفته برای تولید شیر با کیفیت بالا'
        },
        {
          id: 'cat_2',
          name: 'کامیون حمل بار',
          type: 'transport',
          level: 2,
          base_efficiency: 1.4,
          purchase_price: 12000,
          maintenance_cost: 200,
          required_level: 2,
          description: 'کامیون بزرگ برای حمل بارهای سنگین'
        },
        {
          id: 'cat_3',
          name: 'انبار سردخانه',
          type: 'storage',
          level: 2,
          base_efficiency: 1.2,
          purchase_price: 6000,
          maintenance_cost: 80,
          required_level: 2,
          description: 'انبار مجهز به سیستم سرمایش برای نگهداری مواد غذایی'
        },
        {
          id: 'cat_4',
          name: 'دستگاه بسته‌بندی',
          type: 'production',
          level: 1,
          base_efficiency: 1.1,
          purchase_price: 2500,
          maintenance_cost: 40,
          required_level: 1,
          description: 'دستگاه ساده برای بسته‌بندی محصولات'
        }
      ]
      setCatalog(mockCatalog)
    } catch (error) {
      console.error('Error loading catalog:', error)
    }
  }

  const handlePurchaseMachinery = async (catalogItem: MachineryCatalog) => {
    if (!player || player.cash < catalogItem.purchase_price) {
      alert('موجودی نقدی کافی نیست!')
      return
    }

    try {
      const newMachinery: Machinery = {
        id: `mach_${Date.now()}`,
        name: catalogItem.name,
        type: catalogItem.type,
        level: catalogItem.level,
        efficiency: catalogItem.base_efficiency,
        maintenance_cost: catalogItem.maintenance_cost,
        purchase_price: catalogItem.purchase_price,
        condition: 1.0,
        purchased_at: new Date().toISOString(),
        is_active: true
      }
      
      setMachinery([...machinery, newMachinery])
      alert('ماشین‌آلات با موفقیت خریداری شد!')
    } catch (error) {
      console.error('Error purchasing machinery:', error)
    }
  }

  const handleMaintenance = async (machineryId: string) => {
    try {
      setMachinery(machinery.map(mach => 
        mach.id === machineryId 
          ? { ...mach, condition: Math.min(1.0, mach.condition + 0.2) }
          : mach
      ))
      alert('تعمیرات انجام شد!')
    } catch (error) {
      console.error('Error maintaining machinery:', error)
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'production': return '⚙️'
      case 'transport': return '🚚'
      case 'storage': return '📦'
      default: return '🔧'
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'production': return 'تولید'
      case 'transport': return 'حمل و نقل'
      case 'storage': return 'انبار'
      default: return 'عمومی'
    }
  }

  const getConditionColor = (condition: number) => {
    if (condition >= 0.8) return 'text-green-600'
    if (condition >= 0.6) return 'text-yellow-600'
    if (condition >= 0.4) return 'text-orange-600'
    return 'text-red-600'
  }

  const getConditionLabel = (condition: number) => {
    if (condition >= 0.8) return t('machinery.excellent')
    if (condition >= 0.6) return t('machinery.good')
    if (condition >= 0.4) return t('machinery.average')
    return t('machinery.poor')
  }

  const filteredCatalog = selectedType === 'all' 
    ? catalog 
    : catalog.filter(item => item.type === selectedType)

  const totalValue = machinery.reduce((sum, mach) => sum + mach.purchase_price, 0)
  const totalMaintenance = machinery.reduce((sum, mach) => sum + mach.maintenance_cost, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card title={`🔧 ${t('machinery.title')}`} subtitle={t('machinery.subtitle')}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600">
              {t('machinery.yourMachinery')}: {machinery.length} {t('machinery.devices')}
            </p>
            <p className="text-sm text-gray-500">
              {t('machinery.totalValue')}: {totalValue.toLocaleString()} {t('common.price')}
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="input-field"
            >
              <option value="all">{t('machinery.allTypes')}</option>
              <option value="production">{t('machinery.production')}</option>
              <option value="transport">{t('machinery.transport')}</option>
              <option value="storage">{t('machinery.storage')}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Owned Machinery */}
      <Card title={t('machinery.yourMachineryList')} subtitle={t('machinery.yourMachinerySubtitle')}>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : machinery.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {machinery.map((mach) => (
              <div
                key={mach.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center ml-3">
                      <span className="text-blue-600 text-lg">
                        {getTypeIcon(mach.type)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{mach.name}</h3>
                      <p className="text-sm text-gray-600">
                        {getTypeLabel(mach.type)} • {t('machinery.level')} {mach.level}
                      </p>
                    </div>
                  </div>
                  <span className={`text-sm font-medium ${getConditionColor(mach.condition)}`}>
                    {getConditionLabel(mach.condition)}
                  </span>
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('machinery.efficiency')}:</span>
                    <span className="font-medium">{(mach.efficiency * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('machinery.maintenanceCost')}:</span>
                    <span className="font-medium">{mach.maintenance_cost} {t('common.price')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('machinery.condition')}:</span>
                    <span className="font-medium">{(mach.condition * 100).toFixed(0)}%</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="primary"
                    onClick={() => handleMaintenance(mach.id)}
                  >
                    {t('machinery.repair')}
                  </Button>
                  <Button size="sm" variant="secondary">
                    {t('machinery.details')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔧</span>
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t('machinery.noMachinery')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('machinery.noMachineryMessage')}
            </p>
          </div>
        )}
      </Card>

      {/* Machinery Catalog */}
      <Card title={t('machinery.shop')} subtitle={t('machinery.buyNewMachinery')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCatalog.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center ml-3">
                    <span className="text-green-600 text-lg">
                      {getTypeIcon(item.type)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{item.name}</h3>
                    <p className="text-sm text-gray-600">
                      {getTypeLabel(item.type)} • سطح {item.level}
                    </p>
                  </div>
                </div>
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                  سطح {item.required_level} مورد نیاز
                </span>
              </div>

              {item.description && (
                <p className="text-sm text-gray-600 mb-3">{item.description}</p>
              )}

              <div className="space-y-2 mb-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('machinery.efficiency')}:</span>
                  <span className="font-medium">{(item.base_efficiency * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('machinery.maintenanceCost')}:</span>
                  <span className="font-medium">{item.maintenance_cost} {t('common.price')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('machinery.price')}:</span>
                  <span className="font-medium text-green-600">{item.purchase_price.toLocaleString()} {t('common.price')}</span>
                </div>
              </div>

              <Button
                onClick={() => handlePurchaseMachinery(item)}
                disabled={!player || player.cash < item.purchase_price || player.level < item.required_level}
                variant="primary"
                className="w-full"
              >
                {!player || player.cash < item.purchase_price 
                  ? t('machinery.insufficientFunds')
                  : player.level < item.required_level
                  ? t('machinery.insufficientLevel')
                  : t('machinery.buy')
                }
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Machinery Stats */}
      <Card title={t('machinery.statistics')} subtitle={t('machinery.statisticsSubtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <h3 className="text-2xl font-bold text-blue-600">{machinery.length}</h3>
            <p className="text-sm text-blue-800">{t('machinery.deviceCount')}</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <h3 className="text-2xl font-bold text-green-600">{totalValue.toLocaleString()}</h3>
            <p className="text-sm text-green-800">{t('machinery.totalValue')}</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <h3 className="text-2xl font-bold text-purple-600">{totalMaintenance}</h3>
            <p className="text-sm text-purple-800">{t('machinery.maintenanceCost')}</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <h3 className="text-2xl font-bold text-orange-600">
              {machinery.filter(mach => mach.type === 'production').length}
            </h3>
            <p className="text-sm text-orange-800">{t('machinery.productionDevices')}</p>
          </div>
        </div>
      </Card>

      {/* Machinery Benefits */}
      <Card title={t('machinery.benefits')} subtitle={t('machinery.benefitsSubtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start p-4 bg-blue-50 rounded-lg">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-blue-600">⚡</span>
            </div>
            <div>
              <h4 className="font-medium text-blue-800">{t('machinery.efficiencyBoost')}</h4>
              <p className="text-sm text-blue-700">
                {t('machinery.efficiencyBoostDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-green-50 rounded-lg">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-green-600">💰</span>
            </div>
            <div>
              <h4 className="font-medium text-green-800">{t('machinery.costReduction')}</h4>
              <p className="text-sm text-green-700">
                {t('machinery.costReductionDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-purple-50 rounded-lg">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-purple-600">🎯</span>
            </div>
            <div>
              <h4 className="font-medium text-purple-800">{t('machinery.betterQuality')}</h4>
              <p className="text-sm text-purple-700">
                {t('machinery.betterQualityDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-orange-50 rounded-lg">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-orange-600">📈</span>
            </div>
            <div>
              <h4 className="font-medium text-orange-800">{t('machinery.scalability')}</h4>
              <p className="text-sm text-orange-700">
                {t('machinery.scalabilityDescription')}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
